import { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollText, Search, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { auditApi, AuditLogRow, platformTenantsApi, PlatformTenant } from "@/lib/platform-api";
import { ApiError } from "@/lib/api";
import type { ExportTable } from "@/lib/export";
import { ExportMenu } from "@/components/ExportMenu";

const PAGE_SIZE = 25;
const fmt = (iso: string) => new Date(iso).toLocaleString();

const resultClass = (r: string) =>
  r === "SUCCESS" ? "border-success/30 bg-success/10 text-success"
    : r === "FAILURE" ? "border-destructive/30 bg-destructive/10 text-destructive"
    : "border-warning/30 bg-warning/10 text-warning";
const sevClass = (s: string) =>
  s === "CRITICAL" ? "border-destructive/30 bg-destructive/10 text-destructive"
    : s === "WARNING" ? "border-warning/30 bg-warning/10 text-warning"
    : "border-info/30 bg-info/10 text-info";

export default function AuditLogs() {
  const [rows, setRows] = useState<AuditLogRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);

  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const [tenantFilter, setTenantFilter] = useState("all");
  const [severityFilter, setSeverityFilter] = useState("all");
  const [tenants, setTenants] = useState<PlatformTenant[]>([]);
  const [stats, setStats] = useState<{ total: number; critical: number; warning: number } | null>(null);

  useEffect(() => {
    platformTenantsApi.list({ pageSize: 200 }).then((r) => setTenants(r.data)).catch(() => { /* non-fatal */ });
    Promise.all([
      auditApi.platformList({ pageSize: 1 }),
      auditApi.platformList({ pageSize: 1, severity: "CRITICAL" }),
      auditApi.platformList({ pageSize: 1, severity: "WARNING" }),
    ])
      .then(([t, c, w]) => setStats({ total: t.total, critical: c.total, warning: w.total }))
      .catch(() => { /* tiles are best-effort */ });
  }, []);

  // Debounce the search box.
  useEffect(() => {
    const id = setTimeout(() => { setDebounced(search); setPage(1); }, 300);
    return () => clearTimeout(id);
  }, [search]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    auditApi
      .platformList({
        page,
        pageSize: PAGE_SIZE,
        search: debounced || undefined,
        tenantId: tenantFilter === "all" ? undefined : tenantFilter,
        severity: severityFilter === "all" ? undefined : severityFilter,
      })
      .then((r) => { if (!cancelled) { setRows(r.data); setTotal(r.total); } })
      .catch((e) => { if (!cancelled) toast.error(e instanceof ApiError ? e.message : "Failed to load audit logs"); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [page, debounced, tenantFilter, severityFilter]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const buildExportTable = (): ExportTable => ({
    filename: `audit-log_page-${page}`,
    title: "Audit log",
    meta: { Page: String(page), Rows: String(rows.length), Filters: debounced || "none" },
    headers: ["Timestamp", "Actor", "Role", "Tenant", "Action", "Module", "Target", "Result", "Severity", "IP"],
    rows: rows.map((a) => [fmt(a.createdAt), a.actorName, a.actorRole ?? "", a.tenantName ?? "", a.actionType, a.module, a.target ?? "", a.result, a.severity, a.ipAddress ?? ""]),
  });

  const tiles = useMemo(() => ([
    { label: "Total events", value: stats?.total ?? total, tone: "" },
    { label: "Critical", value: stats?.critical ?? 0, tone: "text-destructive" },
    { label: "Warnings", value: stats?.warning ?? 0, tone: "text-warning" },
    { label: "Matching filters", value: total, tone: "" },
  ]), [stats, total]);

  return (
    <div className="space-y-5">
      <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
        {tiles.map((t) => (
          <Card key={t.label} className="p-3">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{t.label}</div>
            <div className={`mt-1 font-display text-xl font-bold tabular-nums ${t.tone}`}>{t.value.toLocaleString()}</div>
          </Card>
        ))}
      </div>

      <Card>
        <CardHeader className="border-b pb-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-2"><ScrollText className="h-5 w-5 text-primary" /><CardTitle className="text-base">Audit trail ({total.toLocaleString()})</CardTitle></div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search actor, action, target..." className="h-9 w-56 pl-8" />
              </div>
              <Select value={tenantFilter} onValueChange={(v) => { setTenantFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 w-44"><SelectValue placeholder="Tenant" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All tenants</SelectItem>
                  {tenants.map((t) => <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={severityFilter} onValueChange={(v) => { setSeverityFilter(v); setPage(1); }}>
                <SelectTrigger className="h-9 w-32"><SelectValue placeholder="Severity" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All</SelectItem>
                  <SelectItem value="INFO">Info</SelectItem>
                  <SelectItem value="WARNING">Warning</SelectItem>
                  <SelectItem value="CRITICAL">Critical</SelectItem>
                </SelectContent>
              </Select>
          <ExportMenu build={buildExportTable} disabled={rows.length === 0} />
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Timestamp</TableHead><TableHead>Actor</TableHead><TableHead>Role</TableHead>
                <TableHead>Tenant</TableHead><TableHead>Action</TableHead><TableHead>Module</TableHead>
                <TableHead>Target</TableHead><TableHead>Result</TableHead><TableHead>Severity</TableHead>
                <TableHead>IP</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={10} className="h-24 text-center text-sm text-muted-foreground"><Loader2 className="mx-auto h-4 w-4 animate-spin" /></TableCell></TableRow>
              ) : rows.length === 0 ? (
                <TableRow><TableCell colSpan={10} className="h-24 text-center text-sm text-muted-foreground">No audit events match your filters.</TableCell></TableRow>
              ) : rows.map((a) => (
                <TableRow key={a.id}>
                  <TableCell className="text-xs font-mono text-muted-foreground whitespace-nowrap">{fmt(a.createdAt)}</TableCell>
                  <TableCell className="text-sm font-medium">{a.actorName}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.actorRole ?? "—"}</TableCell>
                  <TableCell className="text-sm">{a.tenantName ?? "—"}</TableCell>
                  <TableCell className="text-sm">{a.actionType}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{a.module}</TableCell>
                  <TableCell className="text-xs">{a.target ?? "—"}</TableCell>
                  <TableCell><Badge variant="outline" className={resultClass(a.result)}>{a.result}</Badge></TableCell>
                  <TableCell><Badge variant="outline" className={sevClass(a.severity)}>{a.severity}</Badge></TableCell>
                  <TableCell className="text-xs font-mono text-muted-foreground">{a.ipAddress ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-2.5 text-xs text-muted-foreground">
            <span>Page {page} of {totalPages}</span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <Button size="sm" variant="outline" disabled={page >= totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
